import { FC, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { Modal } from "components/Modal";
import { CustomLabel, CustomLabelType } from "types/CustomLabel";
import { getDefaultCustomLabels } from "helpers/customLabels";
import { Delete, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import {
  useAddCustomLabelsMutation,
  useDeleteCustomLabelMutation,
  useUpdateCustomLabelMutation,
  useAddOrganizationCustomLabelMutation,
} from "graphql/__generated__/hooks";

export type CustomLabelEditModalProps = {
  open: boolean;
  title?: string;
  typeKey: CustomLabelType;
  customLabels: CustomLabel[];
  organizationId?: string;
  onClose: () => void;
  onUpdated: (updated: CustomLabel[]) => void;
};

const getDefaultsForType = (typeKey: CustomLabelType): CustomLabel[] => {
  return getDefaultCustomLabels(typeKey);
};

export const CustomLabelEditModal: FC<CustomLabelEditModalProps> = ({
  open,
  title,
  typeKey,
  customLabels,
  organizationId,
  onClose,
  onUpdated,
}) => {
  const [addCustomLabels] = useAddCustomLabelsMutation();
  const [updateCustomLabels] = useUpdateCustomLabelMutation();
  const [deleteCustomLabels] = useDeleteCustomLabelMutation();
  const [addOrganizationCustomLabel] = useAddOrganizationCustomLabelMutation();

  const initialList = useMemo<CustomLabel[]>(() => {
    const base =
      customLabels && customLabels?.length > 0
        ? customLabels
        : getDefaultsForType(typeKey);

    // Ensure type and indices are consistent
    if (base?.length === 0) {
      return base
        .map((item, idx) => ({
          ...item,
          type: typeKey,
          index: idx + 1,
        }))
        .sort((a, b) => a.index - b.index);
    }
    return base
      .map((item) => ({
        ...item,
        type: typeKey,
        index: item.index,
      }))
      .sort((a, b) => a.index - b.index);
  }, [customLabels, typeKey]);

  const [editableList, setEditableList] = useState<CustomLabel[]>(initialList);

  useEffect(() => {
    if (open) {
      setEditableList(initialList);
    }
  }, [open, initialList]);

  const handleChangeLabel = (idx: number, value: string) => {
    setEditableList((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, label: value } : item)),
    );
  };

  const handleAddRow = () => {
    const nextIndex = editableList?.length + 1;
    const newItem: CustomLabel = {
      custom_label_id: `${typeKey}-${nextIndex}`,
      type: typeKey,
      index: nextIndex,
      label: "",
    };
    setEditableList((prev) =>
      [...prev, newItem].map((item, i) => ({ ...item, index: i + 1 })),
    );
  };

  const handleRemoveRow = (idx: number) => {
    const updated = editableList
      .filter((_, i) => i !== idx)
      .map((item, i) => ({
        ...item,
        index: i + 1,
      }));
    setEditableList(updated);
  };

  const handleMoveUp = (idx: number) => {
    if (idx <= 0) return;
    const updated = [...editableList];
    const temp = updated[idx - 1];
    updated[idx - 1] = updated[idx];
    updated[idx] = temp;
    setEditableList(updated.map((item, i) => ({ ...item, index: i + 1 })));
  };

  const handleMoveDown = (idx: number) => {
    if (idx >= editableList?.length - 1) return;
    const updated = [...editableList];
    const temp = updated[idx + 1];
    updated[idx + 1] = updated[idx];
    updated[idx] = temp;
    setEditableList(updated.map((item, i) => ({ ...item, index: i + 1 })));
  };

  const handleSave = () => {
    const run = async () => {
      const cleaned = editableList?.map((item, index) => ({
        ...item,
        index: index + 1,
        type: typeKey,
        custom_label_id: item.custom_label_id,
        label: (item.label || "").trim(),
      }));

      const relevantInitial = (customLabels || []).filter(
        (c) =>
          !c.custom_label_id?.includes("SKILL") &&
          !c.custom_label_id?.includes("TARGET"),
      );
      const initialById = new Map(
        relevantInitial.map((c) => [c.custom_label_id, c]),
      );

      const initialIds = new Set(initialById.keys());

      const editedIds = new Set(cleaned.map((c) => c.custom_label_id));

      // Create: items not present in initial list
      const toCreate = cleaned.filter(
        (c) => !initialIds.has(c.custom_label_id),
      );

      if (toCreate?.length > 0) {
        const addResp = await addCustomLabels({
          variables: {
            objects: toCreate.map((c) => ({
              type: typeKey,
              label: c.label,
              index: c.index,
            })),
          },
        });

        const createdIds: string[] =
          addResp.data?.insert_custom_label?.returning?.map(
            (r: { custom_label_id: string }) => r.custom_label_id,
          ) || [];

        if (organizationId && createdIds?.length > 0) {
          await Promise.all(
            createdIds.map((id) =>
              addOrganizationCustomLabel({
                variables: { org_id: organizationId, custom_label_id: id },
              }),
            ),
          );
        }
      }

      // Update: items present initially and changed (label or index or type)
      const toUpdate = cleaned.filter((c) => {
        if (!initialIds.has(c.custom_label_id)) return false;
        const prev = initialById.get(c.custom_label_id)!;
        return (
          prev.label !== c.label ||
          prev.index !== c.index ||
          prev.type !== c.type
        );
      });
      if (toUpdate?.length > 0) {
        await Promise.all(
          toUpdate.map((c) =>
            updateCustomLabels({
              variables: {
                id: c.custom_label_id,
                custom_label: {
                  type: typeKey,
                  label: c.label,
                  index: c.index,
                },
              },
            }),
          ),
        );
      }

      // Delete: items present initially but not anymore
      const toDeleteIds: string[] = (customLabels || [])
        .filter((c) => !editedIds.has(c.custom_label_id))
        .map((c) => c.custom_label_id);
      if (toDeleteIds.length > 0) {
        await Promise.all(
          toDeleteIds.map((id) =>
            deleteCustomLabels({
              variables: { id },
            }),
          ),
        );
      }

      onUpdated(cleaned);
      onClose();
    };

    run().then();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title ?? "Edit Custom Labels"}
      size="medium"
    >
      <Box sx={{ mt: 1 }}>
        <Stack spacing={1} mt={2}>
          {editableList
            .slice()
            .sort((a, b) => a.index - b.index)
            .map((item, idx) => (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                key={item.custom_label_id}
              >
                <IconButton
                  aria-label="move-up"
                  color="primary"
                  onClick={() => handleMoveUp(idx)}
                  disabled={idx === 0}
                  size="small"
                >
                  <ArrowUpward fontSize="inherit" />
                </IconButton>
                <IconButton
                  aria-label="move-down"
                  onClick={() => handleMoveDown(idx)}
                  disabled={idx === editableList.length - 1}
                  size="small"
                >
                  <ArrowDownward fontSize="inherit" />
                </IconButton>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="dense"
                  name="label"
                  value={item.label}
                  onChange={(e) => handleChangeLabel(idx, e.target.value)}
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => handleRemoveRow(idx)}
                >
                  <Delete />
                </IconButton>
              </Stack>
            ))}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Button variant="text" onClick={handleAddRow}>
            Add row
          </Button>
        </Stack>

        <Grid sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Box mr={2}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Box>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Grid>
      </Box>
    </Modal>
  );
};

export default CustomLabelEditModal;
